import { useEffect, useState } from "react";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import { getApi, putApi, deleteApi } from "../../services/api/globalApi";
import {
  successMessage,
  errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";
import * as XLSX from "xlsx";
import Button from "../../components/common/button/Button";

const STATUS_LABELS = {
  pending: { label: "در انتظار بررسی", color: "bg-yellow-100 text-yellow-800" },
  answered: { label: "پاسخ داده شد", color: "bg-green-100 text-green-800" },
  archived: { label: "بایگانی", color: "bg-gray-100 text-gray-600" },
};

const MAX_MESSAGE_LENGTH = 120;

export default function ContactMessageList() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filter, setFilter] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = async (params) => {
    try {
      const response = await getApi({
        path: "admin/contact-messages",
        params: { ...params },
      });
      setData(response?.data?.result?.data || []);
      setPagination(response?.data?.result);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData({});
  }, []);

  const filterDataHandler = (newParams) => {
    const allFilter = { ...filter, ...newParams };
    setFilter(allFilter);
    fetchData(allFilter);
  };

  const handleStatusChange = async (item, status) => {
    try {
      await putApi({
        path: `admin/contact-messages/${item._id}/status`,
        body: { status },
      });
      successMessage("وضعیت با موفقیت تغییر کرد");
      fetchData(filter);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در تغییر وضعیت");
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoadingExport(true);
      const response = await getApi({
        path: "admin/contact-messages",
        params: { ...filter, limit: 10000, page: 1 },
      });
      const rows = response?.data?.result?.data || [];
      const exportData = rows.map((item) => ({
        "نام": item.name || "-",
        "موبایل": item.mobile || "-",
        "موضوع": item.topic || "-",
        "پیام": item.message || "-",
        "وضعیت": STATUS_LABELS[item.status]?.label || "-",
        "تاریخ ثبت": new Date(item.createdAt).toLocaleDateString("fa-IR"),
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "ContactMessages");
      XLSX.writeFile(workbook, "contact_messages_export.xlsx");
    } catch (error) {
      console.log("Error exporting excel:", error);
      errorMessage("خطا در دریافت خروجی اکسل");
    } finally {
      setLoadingExport(false);
    }
  };

  const handleDelete = async (item) => {
    if (window.confirm(`آیا از حذف پیام "${item.name}" اطمینان دارید؟`)) {
      try {
        await deleteApi({ path: `admin/contact-messages/${item._id}` });
        successMessage("پیام با موفقیت حذف شد");
        fetchData(filter);
      } catch (error) {
        console.log(error);
        errorMessage("خطا در حذف پیام");
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title title={"پیام‌های تماس با ما"} />
        <Button
          onClick={handleExportExcel}
          text={loadingExport ? "در حال دریافت..." : "خروجی اکسل"}
          color="success"
          type="button"
          className="whitespace-nowrap text-xs sm:text-sm"
        />
      </div>

      <div className="mb-4 flex gap-3 flex-wrap">
        <input
          type="text"
          placeholder="جستجو (نام، موبایل، موضوع، متن پیام)..."
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          onChange={(e) =>
            filterDataHandler({ searchText: e.target.value, page: 1 })
          }
        />
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          onChange={(e) =>
            filterDataHandler({ status: e.target.value || undefined, page: 1 })
          }
        >
          <option value="">همه وضعیت‌ها</option>
          <option value="pending">در انتظار بررسی</option>
          <option value="answered">پاسخ داده شد</option>
          <option value="archived">بایگانی</option>
        </select>
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-4">
        {data?.length ? (
          <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">نام</th>
                <th className="px-4 py-3">موبایل</th>
                <th className="px-4 py-3">موضوع</th>
                <th className="px-4 py-3">متن پیام</th>
                <th className="px-4 py-3">وضعیت</th>
                <th className="px-4 py-3">تاریخ ثبت</th>
                <th className="px-4 py-3">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
                const date = new Date(item.createdAt).toLocaleDateString("fa-IR");
                const isExpanded = expandedId === item._id;
                const isLong = (item.message || "").length > MAX_MESSAGE_LENGTH;
                return (
                  <tr
                    key={item._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 dir-ltr text-left">{item.mobile}</td>
                    <td className="px-4 py-3">{item.topic}</td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="whitespace-pre-wrap leading-6">
                        {isExpanded || !isLong
                          ? item.message
                          : `${item.message.substring(0, MAX_MESSAGE_LENGTH)}...`}
                      </p>
                      {isLong && (
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : item._id)}
                          className="text-green-600 hover:text-green-800 font-medium text-xs mt-1"
                        >
                          {isExpanded ? "مشاهده کمتر" : "مشاهده بیشتر"}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusChange(item, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${statusInfo.color}`}
                      >
                        <option value="pending">در انتظار بررسی</option>
                        <option value="answered">پاسخ داده شد</option>
                        <option value="archived">بایگانی</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs">{date}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-xs font-medium text-red-600 dark:text-red-500 hover:underline"
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="w-full flex items-center justify-center my-3 py-2 dark:text-white">
            <p>هیچ پیامی ثبت نشده است.</p>
          </div>
        )}
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />
    </div>
  );
}
